import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAdmin } from '@/hooks/useAdmin'
import ProfileSettings from '@/components/ProfileSettings'
import MyGroups from '@/components/MyGroups'
import MyVendorGroups from '@/components/MyVendorGroups'
import VendorCard from '@/components/VendorCard'
import VendorProfileModal from '@/components/VendorProfile'
import CreateVendorGroupModal from '@/components/CreateVendorGroup'
import CreateAnnouncementModal, { type AnnouncementRow } from '@/components/CreateAnnouncementModal'
import BroadcasterAccessibilityUpload from '@/components/BroadcasterAccessibilityUpload'
import FavouriteChannels from '@/components/FavouriteChannels'
import { format } from 'date-fns'

export default function Profile() {
  const { user, signOut } = useAuth()
  const isAdmin = useAdmin()
  const queryClient = useQueryClient()
  const [showCreateVendorGroupModal, setShowCreateVendorGroupModal] = useState(false)
  const [showVendorProfileModal, setShowVendorProfileModal] = useState(false)
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false)
  const [announcementToEdit, setAnnouncementToEdit] = useState<AnnouncementRow | null>(null)
  const [showRequestDeletionModal, setShowRequestDeletionModal] = useState(false)
  const [deletionConfirmText, setDeletionConfirmText] = useState('')

  // Fetch vendor data for display
  const { data: vendor } = useQuery({
    queryKey: ['vendor', user?.id],
    queryFn: async () => {
      if (!user) return null
      try {
        const { data, error } = await supabase
          .from('vendors')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle()

        if (error && error.code !== 'PGRST116') {
          console.warn('Error fetching vendor:', error)
          return null
        }
        return data
      } catch (err) {
        console.warn('Error fetching vendor:', err)
        return null
      }
    },
    enabled: !!user,
    retry: false,
  })

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user) return null
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle()

      if (error) throw error
      if (data) return data

      // No profile row (e.g. new user from email bypass). RPC creates row with ON CONFLICT DO NOTHING so no 409.
      const displayName =
        user.user_metadata?.full_name ??
        user.email?.split('@')[0] ??
        'User'
      const { data: created, error: rpcError } = await supabase.rpc('ensure_my_profile', {
        p_display_name: displayName,
      })
      if (rpcError) throw rpcError
      const row = Array.isArray(created) ? created[0] : created
      return row ?? null
    },
    enabled: !!user,
    retry: false,
  })

  // Announcements created by this user: vendor's announcements or (if admin with no vendor) platform announcements
  const canManageAnnouncements = Boolean(vendor || isAdmin)
  const { data: myAnnouncements = [] } = useQuery({
    queryKey: ['my-announcements', user?.id, vendor?.id, isAdmin],
    queryFn: async () => {
      if (!user || !canManageAnnouncements) return []
      const q = supabase
        .from('announcements')
        .select('id, vendor_id, announcement_text, expiry_days, image_url, created_at, expires_at')
        .order('created_at', { ascending: false })
      if (vendor) {
        const { data, error } = await q.eq('vendor_id', vendor.id)
        if (error) return []
        return (data ?? []) as AnnouncementRow[]
      }
      if (isAdmin) {
        const { data, error } = await q.is('vendor_id', null)
        if (error) return []
        return (data ?? []) as AnnouncementRow[]
      }
      return []
    },
    enabled: Boolean(user && canManageAnnouncements),
  })

  const deleteAnnouncementMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('announcements').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] })
      queryClient.invalidateQueries({ queryKey: ['my-announcements'] })
    },
    onError: (err: Error) => {
      alert(`Failed to delete announcement: ${err.message}`)
    },
  })

  const handleDeleteAnnouncement = (id: string) => {
    if (!window.confirm('Delete this announcement? This cannot be undone.')) return
    deleteAnnouncementMutation.mutate(id)
  }

  const requestDeletionMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('request_account_deletion')
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] })
      setShowRequestDeletionModal(false)
      setDeletionConfirmText('')
      signOut()
    },
    onError: (err: Error) => {
      alert(`Failed to request account deletion: ${err.message}`)
    },
  })

  const cancelDeletionRequestMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc('cancel_deletion_request')
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] })
    },
    onError: (err: Error) => {
      alert(`Failed to cancel request: ${err.message}`)
    },
  })

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto">
        <p>Please log in to view your profile.</p>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto">
        <p>Loading profile...</p>
      </div>
    )
  }

  const userType = profile?.user_type || profile?.role || 'normal'
  const isVendorUser = userType === 'broadcaster' || userType === 'ad-creator'

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 
          className="text-3xl font-bold"
          style={{ color: 'var(--text-primary)' }}
        >
          Profile
        </h1>
        <button
          onClick={signOut}
          className="px-6 py-2 font-semibold rounded"
          style={{
            backgroundColor: 'var(--color-error)',
            color: 'var(--text-inverse)',
          }}
        >
          Sign Out
        </button>
      </div>
      
      <div className="mb-4">
        <p style={{ color: 'var(--text-primary)' }}>
          Signed in as {user.email}
        </p>
        <p style={{ color: 'var(--text-primary)' }}>
          User Type: {userType === 'admin' ? 'Admin' : userType === 'ad-creator' ? 'Ad-Creator' : userType === 'broadcaster' ? 'Broadcaster' : userType === 'moderator' ? 'Moderator' : 'Normal'}
        </p>
      </div>

      {isVendorUser && (
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => setShowVendorProfileModal(true)}
            className="px-6 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-interactive)'
            }}
          >
            {vendor ? 'Edit Vendor Profile' : 'Create Vendor Profile'}
          </button>
          <button
            onClick={() => setShowCreateVendorGroupModal(true)}
            className="px-6 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-interactive)'
            }}
          >
            Create Vendor Group
          </button>
          {(vendor || isAdmin) && (
            <button
              onClick={() => {
                setAnnouncementToEdit(null)
                setShowAnnouncementModal(true)
              }}
              className="px-6 py-2 font-semibold rounded"
              style={{
                backgroundColor: 'var(--bg-interactive)',
                color: 'var(--text-inverse)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--bg-interactive)'
              }}
            >
              Create Announcement
            </button>
          )}
        </div>
      )}

      {/* Admin without vendor profile: Create platform announcement */}
      {isAdmin && !isVendorUser && (
        <div className="mb-6 flex gap-4">
          <button
            onClick={() => {
              setAnnouncementToEdit(null)
              setShowAnnouncementModal(true)
            }}
            className="px-6 py-2 font-semibold rounded"
            style={{
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-interactive-hover)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--bg-interactive)'
            }}
          >
            Create Announcement
          </button>
        </div>
      )}

      {/* Your announcements: list with Edit (creators only) */}
      {canManageAnnouncements && myAnnouncements.length > 0 && (
        <div className="mb-6">
          <h2 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            Your announcements
          </h2>
          <ul className="space-y-3">
            {myAnnouncements.map((a) => (
              <li
                key={a.id}
                className="rounded-lg p-3 border-2 flex items-center justify-between gap-4"
                style={{
                  backgroundColor: 'var(--bg-secondary)',
                  borderColor: 'var(--bg-tertiary)',
                }}
              >
                <div className="min-w-0 flex-1">
                  <p
                    className="text-sm truncate"
                    style={{ color: 'var(--text-primary)' }}
                    title={a.announcement_text}
                  >
                    {a.announcement_text}
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                    Created {format(new Date(a.created_at!), 'dd/MM/yyyy')}
                    {a.expires_at && ` · Expires ${format(new Date(a.expires_at), 'dd/MM/yyyy')}`}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      setAnnouncementToEdit(a)
                      setShowAnnouncementModal(true)
                    }}
                    className="px-4 py-2 text-sm font-medium rounded"
                    style={{
                      backgroundColor: 'var(--bg-interactive)',
                      color: 'var(--text-inverse)',
                    }}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteAnnouncement(a.id)}
                    disabled={deleteAnnouncementMutation.isPending}
                    className="px-4 py-2 text-sm font-medium rounded"
                    style={{
                      backgroundColor: 'var(--color-error)',
                      color: 'var(--text-inverse)',
                      opacity: deleteAnnouncementMutation.isPending ? 0.6 : 1,
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {userType === 'broadcaster' && (
        <div className="mb-6">
          <BroadcasterAccessibilityUpload userId={user.id} />
        </div>
      )}

      <FavouriteChannels userId={user.id} />

      {isVendorUser ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <ProfileSettings profile={profile} />
            {vendor && (
              <VendorCard 
                vendor={{
                  ...vendor,
                  vendor_type: userType,
                }}
              />
            )}
          </div>
          <div className="space-y-6">
            <MyGroups userId={user.id} />
            <MyVendorGroups userId={user.id} userType={userType} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <ProfileSettings profile={profile} />
          <MyGroups userId={user.id} />
        </div>
      )}

      {/* Account deletion: at bottom of page */}
      {profile?.deletion_requested_at && (
        <div
          className="mb-6 p-4 rounded-lg border-2"
          role="alert"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--color-error)',
            color: 'var(--text-primary)',
          }}
        >
          <p className="font-semibold mb-2">Account deletion requested</p>
          <p className="text-sm mb-3">
            Your account is pending deletion. An admin will process your request. If this was a mistake, you can cancel the request below. For help, contact{' '}
            <a href="mailto:support@insideaccess.org" className="underline" style={{ color: 'var(--text-primary)' }}>support@insideaccess.org</a>.
          </p>
          <button
            type="button"
            onClick={() => cancelDeletionRequestMutation.mutate()}
            disabled={cancelDeletionRequestMutation.isPending}
            className="px-4 py-2 text-sm font-medium rounded border"
            style={{
              borderColor: 'var(--bg-interactive)',
              backgroundColor: 'var(--bg-interactive)',
              color: 'var(--text-inverse)',
            }}
          >
            {cancelDeletionRequestMutation.isPending ? 'Cancelling…' : 'Cancel deletion request'}
          </button>
        </div>
      )}

      {!profile?.deletion_requested_at && (
        <div className="mb-6">
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Account deletion</h2>
          <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
            You can request that your account be deleted. An administrator will process the request. Your content may be reassigned to the platform. See the{' '}
            <Link to="/privacy-policy" className="underline" style={{ color: 'var(--text-primary)' }}>Privacy Policy</Link> and contact{' '}
            <a href="mailto:support@insideaccess.org" className="underline" style={{ color: 'var(--text-primary)' }}>support@insideaccess.org</a> with questions.
          </p>
          <button
            type="button"
            onClick={() => setShowRequestDeletionModal(true)}
            className="px-4 py-2 text-sm font-medium rounded border"
            style={{
              borderColor: 'var(--color-error)',
              backgroundColor: 'transparent',
              color: 'var(--color-error)',
            }}
          >
            Request account deletion
          </button>
        </div>
      )}

      {showVendorProfileModal && (
        <VendorProfileModal
          userId={user.id}
          userType={userType}
          onClose={() => setShowVendorProfileModal(false)}
          onSuccess={() => {
            // Profile update success is handled by query invalidation
          }}
        />
      )}

      {showCreateVendorGroupModal && (
        <CreateVendorGroupModal
          userType={userType}
          onClose={() => setShowCreateVendorGroupModal(false)}
          onSuccess={() => {
            // Group creation success is handled by query invalidation
          }}
        />
      )}

      {showAnnouncementModal && (vendor || isAdmin) && (
        <CreateAnnouncementModal
          vendorId={announcementToEdit?.vendor_id ?? vendor?.id ?? null}
          onClose={() => {
            setShowAnnouncementModal(false)
            setAnnouncementToEdit(null)
          }}
          onSuccess={() => {
            // Success is handled by query invalidation in modal
          }}
          existing={announcementToEdit}
        />
      )}

      {showRequestDeletionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="request-deletion-title"
        >
          <div
            className="max-w-md w-full rounded-lg border-2 p-6"
            style={{
              backgroundColor: 'var(--bg-primary)',
              borderColor: 'var(--bg-tertiary)',
            }}
          >
            <h2 id="request-deletion-title" className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
              Request account deletion
            </h2>
            <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
              Your account will be marked for deletion. An administrator will approve or reject the request. Once approved, you will not be able to sign in and your content may be reassigned. You will be signed out after submitting.
            </p>
            <p className="text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
              Type <strong>DELETE</strong> below to confirm.
            </p>
            <input
              type="text"
              value={deletionConfirmText}
              onChange={(e) => setDeletionConfirmText(e.target.value)}
              placeholder="DELETE"
              className="w-full px-3 py-2 rounded border-2 mb-4"
              style={{
                borderColor: 'var(--bg-tertiary)',
                backgroundColor: 'var(--bg-primary)',
                color: 'var(--text-primary)',
              }}
              aria-label="Type DELETE to confirm"
            />
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowRequestDeletionModal(false)
                  setDeletionConfirmText('')
                }}
                className="px-4 py-2 rounded border-2"
                style={{
                  borderColor: 'var(--bg-tertiary)',
                  backgroundColor: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => requestDeletionMutation.mutate()}
                disabled={deletionConfirmText !== 'DELETE' || requestDeletionMutation.isPending}
                className="px-4 py-2 rounded font-medium"
                style={{
                  backgroundColor: deletionConfirmText === 'DELETE' ? 'var(--color-error)' : 'var(--bg-tertiary)',
                  color: 'var(--text-inverse)',
                  opacity: deletionConfirmText !== 'DELETE' ? 0.7 : 1,
                }}
              >
                {requestDeletionMutation.isPending ? 'Submitting…' : 'Submit request'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

